import { type FormikProps, getIn } from 'formik';
import React from 'react';
import {
	Alert,
	Button,
	FormControl,
	Typography,
	Stack,
	DialogContent,
	DialogActions,
	FormHelperText,
} from '@mui/material';

import { PreferredAssessmentDatePopUp } from '../../../components/request';
import Dialog from '../../../components/dialog';
import { fDateTime } from '../../../utils/formatTime';
import { messages } from '../../../utils/messages';
import { calculateNextValidDate } from '../../../utils/assessmentDateTimeCalculator';

interface PrefAssessmentDateValues {
	prefAssessmentDates: (Date | null | undefined)[];
}

interface PrefAssessmentDateStepProps<T extends PrefAssessmentDateValues> {
	formik: FormikProps<T>;
	prefAssessmentDateLabels: string[];
}

export const PreferredAssessmentDates = <T extends PrefAssessmentDateValues>({
	formik,
	prefAssessmentDateLabels,
}: PrefAssessmentDateStepProps<T>) => {
	const [open, setOpen] = React.useState(false);
	const [selectedDateIndex, setSelectedDateIndex] = React.useState<number | null>(null);
	const [localDate, setLocalDate] = React.useState<Date>(new Date());

	const handleCloseAssessmentDateDialog = () => {
		setOpen(false);
	};

	const handleConfirmAssessmentDateDialog = () => {
		if (selectedDateIndex !== null) {
			formik.setFieldValue(`prefAssessmentDates[${selectedDateIndex}]`, localDate);
		}
		setOpen(false);
	};

	const onDateTimeLabelClick = (dateTimeIndex: number) => {
		setLocalDate(formik.values.prefAssessmentDates[dateTimeIndex] ?? calculateNextValidDate(new Date()));
		setSelectedDateIndex(dateTimeIndex);
		setOpen(true);
	};

	const ifError = getIn(formik.errors, 'prefAssessmentDates');

	return (
		<>
			{/* error prop sets FormHelperText to the associated error color in palette */}
			<FormControl margin="normal" component="fieldset" variant="standard" error={Boolean(ifError)}>
				<Stack
					spacing={2}
					sx={{
						alignItems: 'flex-start',
					}}
				>
					<Alert severity="info">{messages['pref-assessment-date']}</Alert>
					{prefAssessmentDateLabels.map((label, index) => (
						<React.Fragment key={label}>
							<Typography variant="subtitle2" color="grey.500">
								{label}
							</Typography>
							<Button
								variant={formik.values.prefAssessmentDates[index] ? 'text' : 'outlined'}
								onClick={() => onDateTimeLabelClick(index)}
								sx={{
									justifyContent: 'flex-start',
									px: formik.values.prefAssessmentDates[index] ? 0 : 2,
									textTransform: 'none',
								}}
							>
								{formik.values.prefAssessmentDates[index]
									? fDateTime(formik.values.prefAssessmentDates[index])
									: 'Select a Date'}
							</Button>
						</React.Fragment>
					))}
					{Boolean(ifError) && <FormHelperText>{ifError}</FormHelperText>}
				</Stack>
				<Dialog open={open} onClose={handleCloseAssessmentDateDialog}>
					<DialogContent>
						<PreferredAssessmentDatePopUp
							reminder={messages['pref-assessment-date']}
							initialDate={localDate}
							onChange={setLocalDate}
						/>
					</DialogContent>

					<DialogActions>
						<Stack direction="row" spacing={4} justifyContent="flex-end">
							<Button onClick={handleCloseAssessmentDateDialog}>Cancel</Button>
							<Button onClick={handleConfirmAssessmentDateDialog} variant="contained" color="primary">
								Confirm
							</Button>
						</Stack>
					</DialogActions>
				</Dialog>
			</FormControl>
		</>
	);
};
